// src/pages/ELearning/HRDashboardPage.tsx
import React, { useEffect, useState } from 'react';
import { getHROverview } from '../../api/client';
import { Loader2, Users, CheckCircle, AlertTriangle, TrendingUp, BarChart3, Award } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#2575fc', '#10b981', '#f59e0b', '#e74c3c', '#8b5cf6', '#06b6d4'];

const HRDashboardPage: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await getHROverview();
        setData(d);
      } catch (e: any) {
        console.error(e);
        setError(e.response?.data?.error || 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} className="spin" color="#2575fc" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ padding: 20, background: 'rgba(231,76,60,0.1)', borderRadius: 12, color: '#e74c3c' }}>
          {error}
        </div>
      </>
    );
  }

  if (!data) return <><p>Нет данных</p></>;

  const stats = data.stats || {};
  const byDepartment = data.byDepartment || [];
  const laggards = data.laggards || [];
  const leaders = data.leaders || [];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <BarChart3 size={32} color="#2575fc" />
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, color: '#1a1a2e' }}>
          HR Дашборд обучения
        </h1>
      </div>

      {/* ═══ KPI ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <KPI icon={<Users size={24} color="#2575fc" />} label="Сотрудники" value={stats.totalEmployees || 0} color="#2575fc" />
        <KPI icon={<CheckCircle size={24} color="#10b981" />} label="Пройдено тестов" value={stats.passedResults || 0} color="#10b981" />
        <KPI icon={<TrendingUp size={24} color="#8b5cf6" />} label="Средний балл" value={(stats.avgScore || 0) + '%'} color="#8b5cf6" />
        <KPI icon={<AlertTriangle size={24} color="#e74c3c" />} label="Просрочено" value={stats.overdueAssignments || 0} color="#e74c3c" />
        <KPI icon={<Award size={24} color="#f59e0b" />} label="Назначений" value={stats.totalAssignments || 0} color="#f59e0b" />
      </div>

      {/* ═══ По отделам ═══ */}
      {byDepartment.length > 0 && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>Обучение по отделам</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byDepartment}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="passedCount" name="Пройдено" fill="#10b981" />
              <Bar dataKey="assignmentsOverdue" name="Просрочено" fill="#e74c3c" />
              <Bar dataKey="assignmentsCompleted" name="Назначения завершены" fill="#2575fc" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ═══ Laggards + Leaders ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginTop: 24 }}>
        <div style={card}>
          <h3 style={{ marginTop: 0, color: '#e74c3c' }}>⚠️ Отстающие</h3>
          {laggards.length === 0 ? (
            <p style={{ color: '#999', fontSize: 14 }}>Нет отстающих</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {laggards.map((u: any, idx: number) => (
                <div key={u.id} style={userRow}>
                  <div>
                    <strong>{idx + 1}. {u.name}</strong>
                    <div style={{ fontSize: 12, color: '#888' }}>{u.email}</div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12 }}>
                    {u.overdue > 0 && <div style={{ color: '#e74c3c', fontWeight: 700 }}>⚠️ {u.overdue} просрочено</div>}
                    <div style={{ color: '#666' }}>{u.activeAssignments} активных</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={card}>
          <h3 style={{ marginTop: 0, color: '#10b981' }}>🏆 Лидеры</h3>
          {leaders.length === 0 ? (
            <p style={{ color: '#999', fontSize: 14 }}>Нет данных</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {leaders.map((u: any, idx: number) => (
                <div key={u.id} style={userRow}>
                  <div>
                    <strong>{idx + 1}. {u.name}</strong>
                    <div style={{ fontSize: 12, color: '#888' }}>{u.passedTests} пройдено</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, color: '#10b981' }}>{u.avgScore}%</div>
                    <div style={{ fontSize: 11, color: '#666' }}>средний</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const KPI: React.FC<{ icon: React.ReactNode; label: string; value: any; color: string }> = ({ icon, label, value, color }) => (
  <div style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.5)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)', textAlign: 'center' }}>
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>{icon}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color: '#1a1a2e' }}>{value}</div>
    <div style={{ fontSize: 12, color, fontWeight: 600 }}>{label}</div>
  </div>
);

const card: React.CSSProperties = { background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.5)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' };
const userRow: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'rgba(255,255,255,0.5)', borderRadius: 10 };

export default HRDashboardPage;
